import { useState, useCallback, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, Download, Trash2, Copy, Check, FileIcon, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component
const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_upload-drop/artifacts/zlaghso5_sonoarte_logo.png" 
    alt="Sonoarte" 
    className="h-16 sm:h-20 w-auto"
    data-testid="logo"
  />
);

// Header component
const Header = ({ showAdminLink = true }) => {
  const navigate = useNavigate();
  
  return (
    <header className="flex flex-col items-center pt-12 sm:pt-16 pb-8">
      <div className="cursor-pointer" onClick={() => navigate('/')}>
        <Logo />
      </div>
      <p className="mt-6 text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">
        ESTUDIO DE GRABACIÓN • TRANSFERENCIA DE ARCHIVOS
      </p>
      {showAdminLink && (
        <Link 
          to="/admin" 
          className="mt-4 text-xs tracking-wide text-zinc-400 hover:text-pink-500 transition-colors"
          data-testid="admin-link"
        >
          Gestionar archivos
        </Link>
      )}
    </header>
  );
};

// Footer component
const Footer = () => (
  <footer className="fixed bottom-0 left-0 right-0 py-6 text-center">
    <p className="text-xs tracking-[0.15em] text-zinc-400">
      SONOARTE.NET • 2026
    </p>
  </footer>
);

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Upload Page
const UploadPage = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [shareLink, setShareLink] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, []);

  const validateAndSetFile = (selectedFile) => {
    setError(null);
    const maxSize = 1024 * 1024 * 1024; // 1GB
    if (selectedFile.size > maxSize) {
      setError("El archivo excede el tamaño máximo de 1GB");
      return;
    }
    setFile(selectedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      const fullLink = `${window.location.origin}${response.data.share_link}`;
      setShareLink(fullLink);
      setUploadedFileName(file.name);
      toast.success("Archivo subido correctamente");
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || "Error al subir el archivo");
      toast.error("Error al subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const copyLink = async () => {
    const textToCopy = shareLink;
    
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success("Link copiado al portapapeles");
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.log('Clipboard API failed, trying fallback');
      }
    }
    
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;boxShadow:none;background:transparent;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success("Link copiado al portapapeles");
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      console.log('execCommand fallback failed');
    }
    
    toast.info("Selecciona y copia el enlace manualmente");
    window.prompt("Copia este enlace (Ctrl+C):", textToCopy);
  };

  const resetUpload = () => {
    setFile(null);
    setShareLink(null);
    setUploadedFileName(null);
    setProgress(0);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-start justify-center px-6 pb-24">
        <div className="w-full max-w-xl">
          {!shareLink ? (
            <>
              <div
                className={`min-h-[320px] w-full border rounded-3xl flex flex-col items-center justify-center p-12 cursor-pointer transition-all duration-500 ${
                  isDragging 
                    ? 'border-2 border-pink-500 bg-pink-50/30' 
                    : 'border-zinc-200 hover:border-pink-500 hover:bg-pink-50/30'
                } ${file ? 'bg-zinc-50' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !file && document.getElementById('file-input').click()}
                data-testid="upload-dropzone"
              >
                <input
                  type="file"
                  id="file-input"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />

                {!file && !uploading && (
                  <>
                    <div className="w-16 h-16 rounded-full border border-zinc-200 flex items-center justify-center mb-6 group-hover:border-pink-500 transition-colors">
                      <Upload className="w-6 h-6 text-pink-500" />
                    </div>
                    <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium text-zinc-900 mb-3">
                      Enviar material
                    </h2>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-pink-500">
                      NUBE PRIVADA SONOARTE • MÁX 1GB
                    </p>
                  </>
                )}

                {file && !uploading && !shareLink && (
                  <div className="text-center">
                    <FileIcon className="w-12 h-12 text-pink-500 mx-auto mb-4" />
                    <p className="font-medium text-zinc-900 mb-1">{file.name}</p>
                    <p className="text-sm text-zinc-500 mb-6">{formatFileSize(file.size)}</p>
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); resetUpload(); }}
                        className="px-6 py-3 border border-zinc-200 text-zinc-900 rounded-full font-medium hover:border-pink-500 hover:text-pink-500 transition-colors"
                        data-testid="cancel-button"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUpload(); }}
                        className="px-8 py-3 bg-pink-500 text-white rounded-full font-medium tracking-wide hover:bg-pink-600 transition-colors shadow-[0_8px_32px_rgba(236,72,153,0.2)] hover:shadow-[0_8px_32px_rgba(236,72,153,0.4)]"
                        data-testid="upload-button"
                      >
                        Subir archivo
                      </button>
                    </div>
                  </div>
                )}

                {uploading && (
                  <div className="w-full text-center">
                    <Loader2 className="w-12 h-12 text-pink-500 mx-auto mb-4 animate-spin" />
                    <p className="font-medium text-zinc-900 mb-4">Subiendo {file.name}...</p>
                    <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden mb-2">
                      <div 
                        className="h-full bg-pink-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                        data-testid="progress-bar"
                      />
                    </div>
                    <p className="text-sm text-zinc-500">{progress}%</p>
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </>
          ) : (
            <div className="min-h-[320px] w-full border border-zinc-200 rounded-3xl flex flex-col items-center justify-center p-12">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium text-zinc-900 mb-2">
                Archivo subido
              </h2>
              <p className="text-sm text-zinc-500 mb-6">Pulsa sobre el nombre para acceder a la descarga</p>
              
              <a 
                href={shareLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 bg-zinc-50 hover:bg-pink-50 rounded-2xl px-6 py-4 mb-6 transition-colors"
                data-testid="share-link"
              >
                <FileIcon className="w-5 h-5 text-pink-500" />
                <span className="font-medium text-zinc-900 group-hover:text-pink-600 transition-colors">
                  {uploadedFileName}
                </span>
                <ExternalLink className="w-4 h-4 text-zinc-400 group-hover:text-pink-500 transition-colors" />
              </a>

              <div className="flex gap-4">
                <button
                  onClick={copyLink}
                  className="px-8 py-3 bg-pink-500 text-white rounded-full font-medium tracking-wide hover:bg-pink-600 transition-colors shadow-[0_8px_32px_rgba(236,72,153,0.2)] hover:shadow-[0_8px_32px_rgba(236,72,153,0.4)] flex items-center gap-2"
                  data-testid="copy-link-button"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiado' : 'Copiar enlace'}
                </button>
                <button
                  onClick={resetUpload}
                  className="px-6 py-3 border border-zinc-200 text-zinc-900 rounded-full font-medium hover:border-pink-500 hover:text-pink-500 transition-colors"
                  data-testid="new-upload-button"
                >
                  Subir otro
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Download Page
const DownloadPage = () => {
  const { fileId } = useParams();
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFileInfo = async () => {
      try {
        const response = await axios.get(`${API}/files/${fileId}`);
        setFileInfo(response.data);
      } catch (err) {
        setError("Archivo no encontrado o ya no está disponible");
      } finally {
        setLoading(false);
      }
    };
    fetchFileInfo();
  }, [fileId]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header showAdminLink={false} />
      
      <main className="flex-1 flex items-start justify-center px-6 pb-24">
        <div className="w-full max-w-xl">
          <div className="min-h-[320px] w-full border border-zinc-200 rounded-3xl flex flex-col items-center justify-center p-12">
            {loading && (
              <Loader2 className="w-12 h-12 text-pink-500 animate-spin" />
            )}

            {error && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium text-zinc-900 mb-2">
                  Archivo no disponible
                </h2>
                <p className="text-sm text-zinc-500">{error}</p>
              </>
            )}

            {fileInfo && !error && (
              <>
                <div className="w-16 h-16 rounded-full border border-zinc-200 flex items-center justify-center mb-6">
                  <Download className="w-6 h-6 text-pink-500" />
                </div>
                <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium text-zinc-900 mb-2 text-center">
                  {fileInfo.original_filename}
                </h2>
                <p className="text-sm text-zinc-500 mb-8">{formatFileSize(fileInfo.size)}</p>
                
                <a
                  href={`${API}/files/${fileId}/download`}
                  download={fileInfo.original_filename}
                  className="px-10 py-4 bg-pink-500 text-white rounded-full font-medium tracking-wide hover:bg-pink-600 transition-colors shadow-[0_8px_32px_rgba(236,72,153,0.2)] hover:shadow-[0_8px_32px_rgba(236,72,153,0.4)] flex items-center gap-2 no-underline"
                  data-testid="download-button"
                  onClick={() => toast.success("Descarga iniciada")}
                >
                  <Download className="w-5 h-5" />
                  Descargar archivo
                </a>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/files`);
      setFiles(response.data);
    } catch (err) {
      toast.error("Error al cargar los archivos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`¿Eliminar "${fileName}"?`)) return;
    
    setDeleting(fileId);
    try {
      await axios.delete(`${API}/files/${fileId}`);
      setFiles(files.filter(f => f.id !== fileId));
      toast.success("Archivo eliminado");
    } catch (err) {
      toast.error("Error al eliminar el archivo");
    } finally {
      setDeleting(null);
    }
  };

  const copyLink = async (fileId) => {
    const link = `${window.location.origin}/d/${fileId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado");
    } catch (err) {
      toast.error("Error al copiar");
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header showAdminLink={false} />
      
      <main className="flex-1 px-6 pb-24">
        <div className="w-full max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-outfit text-2xl sm:text-3xl tracking-tight font-medium text-zinc-900">
              Archivos subidos
            </h1>
            <Link 
              to="/"
              className="px-6 py-2 border border-zinc-200 text-zinc-900 rounded-full text-sm font-medium hover:border-pink-500 hover:text-pink-500 transition-colors"
              data-testid="back-to-upload"
            >
              Subir archivo
            </Link>
          </div>

          {loading && (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
            </div>
          )}

          {!loading && files.length === 0 && (
            <div className="text-center py-16 border border-zinc-200 rounded-3xl">
              <FileIcon className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
              <p className="text-zinc-500">No hay archivos subidos</p>
            </div>
          )}

          {!loading && files.length > 0 && (
            <div className="border border-zinc-200 rounded-3xl overflow-hidden">
              <table className="w-full text-left" data-testid="files-table">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-6 py-4 text-xs tracking-[0.1em] uppercase font-bold text-zinc-500">Archivo</th>
                    <th className="px-6 py-4 text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 hidden sm:table-cell">Tamaño</th>
                    <th className="px-6 py-4 text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 hidden md:table-cell">Descargas</th>
                    <th className="px-6 py-4 text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 hidden lg:table-cell">Fecha</th>
                    <th className="px-6 py-4 text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr 
                      key={file.id} 
                      className="border-t border-zinc-100 hover:bg-zinc-50/50 transition-colors"
                      data-testid={`file-row-${file.id}`}
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-zinc-900 truncate max-w-[200px] sm:max-w-[300px]">
                          {file.original_filename}
                        </p>
                        <p className="text-xs text-zinc-400 sm:hidden">{formatFileSize(file.size)}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-500 hidden sm:table-cell">
                        {formatFileSize(file.size)}
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-500 hidden md:table-cell">
                        <span className="inline-flex items-center gap-1">
                          <Download className="w-3 h-3" />
                          {file.download_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-500 hidden lg:table-cell">
                        {formatDate(file.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => copyLink(file.id)}
                            className="p-2 text-zinc-400 hover:text-pink-500 transition-colors"
                            title="Copiar enlace"
                            data-testid={`copy-button-${file.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(file.id, file.original_filename)}
                            disabled={deleting === file.id}
                            className="p-2 text-zinc-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            title="Eliminar"
                            data-testid={`delete-button-${file.id}`}
                          >
                            {deleting === file.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<UploadPage />} />
          <Route path="/d/:fileId" element={<DownloadPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
